import {parse} from 'qs';
import Url from 'url-parse';
import {KakaoentPolicyType} from '../../../domain/policy/type';
import {getKakaoentPolicyUrl} from '../../../domain/policy/util/getKakaoentPolicyUrl';
import {requestContext} from '../../requestContext';
import {InvalidPathError} from './InvalidPathError.class';
import {MalformedPathError} from './MalformedPathError.class';
import {PagewebUrl} from './PagewebUrl.class';

export function getOpenWebviewRedirectPagewebUrl({
  url,
}: {
  webOrigin: string;
  url: string;
}): PagewebUrl {
  const {pathname: _pathname, query: _query} = new Url(url);

  const pathname = _pathname.replace(/(^\/|\/$)/g, '');
  const splittedPathList = pathname.split('/');
  const querystring = _query.replace(/^\?/g, '');
  const parsedQueryparam = parse(querystring);

  if (!pathname.startsWith('open/webview') || splittedPathList.length < 3) {
    throw new InvalidPathError();
  }

  let origin;
  let path;
  let queryparam;

  const actionPath = splittedPathList[2];
  switch (actionPath) {
    case 'dictionary':
      {
        path = '/redirect-dictionary';
        queryparam = parsedQueryparam;
      }
      break;
    case 'ticket_history':
      {
        path = '/history/ticket';
        queryparam = parsedQueryparam;
      }
      break;
    case 'content_ticket_history':
      {
        const {series_id, ...rest} = parsedQueryparam;
        path = `/content/${series_id}/history/ticket`;
        queryparam = rest;
      }
      break;
    case 'ticket_refund':
      {
        const {series_id, ...rest} = parsedQueryparam;
        if (!series_id) {
          throw new MalformedPathError();
        }

        path = `/content/${series_id}/ticket-refund`;
        queryparam = rest;
      }
      break;
    case 'cash_history':
      {
        path = '/history/cash';
        queryparam = parsedQueryparam;
      }
      break;
    case 'content_buy_history':
      {
        const {series_id, ...rest} = parsedQueryparam;
        if (!series_id) {
          throw new MalformedPathError();
        }

        path = `/content/${series_id}/history/buy`;
        queryparam = rest;
      }
      break;
    case 'redeem':
      {
        path = '/redeem';
        queryparam = parsedQueryparam;
      }
      break;
    case 'notice':
      {
        const {notice_uid, notice_page, ...rest} = parsedQueryparam;

        path = `/notice${notice_uid ? `/${notice_uid}` : ''}`;
        queryparam = {
          ...rest,
          // notice_uid가 있으면 notice_page를 무시합니다.
          notice_page: notice_uid ? undefined : notice_page,
        };
      }
      break;
    case 'help':
      {
        const {help_uid, ...rest} = parsedQueryparam;

        path = `/help${help_uid ? `/${help_uid}` : ''}`;
        queryparam = rest;
      }
      break;
    case 'business_info':
      {
        path = 'business-info';
        queryparam = parsedQueryparam;
      }
      break;
    case 'policy':
      {
        if (splittedPathList.length < 4) {
          throw new MalformedPathError();
        }
        const policyType = splittedPathList[3];
        switch (policyType) {
          case 'terms':
            {
              path = '/policy/terms';
              queryparam = parsedQueryparam;
            }
            break;
          case 'terms_paid':
            {
              path = '/policy/terms-paid';
              queryparam = parsedQueryparam;
            }
            break;
          case 'privacy':
            {
              const urlObj = getKakaoentPolicyUrl({
                type: KakaoentPolicyType.Policy,
              });
              const url = requestContext.requestInfo.display.isWebView
                ? urlObj.onlyContentUrl()
                : urlObj.urlExcludeVod();
              origin = url.origin;
              path = url.pathname;
              queryparam = {...parse(url.query), ...parsedQueryparam};
            }
            break;
          case 'operation':
            {
              path = '/policy/operation';
              queryparam = parsedQueryparam;
            }
            break;
          case 'safeguard':
            {
              path = '/policy/safeguard';
              queryparam = parsedQueryparam;
            }
            break;
          case 'right':
            {
              path = '/policy/right';
              queryparam = parsedQueryparam;
            }
            break;
          case 'personal_ad':
            {
              const urlObj = getKakaoentPolicyUrl({
                type: KakaoentPolicyType.TargetAd,
              });
              const url = requestContext.requestInfo.display.isWebView
                ? urlObj.onlyContentUrl()
                : urlObj.urlExcludeVod();
              origin = url.origin;
              path = url.pathname;
              queryparam = {...parse(url.query), ...parsedQueryparam};
            }
            break;
          case 'marketing_third':
            {
              path = '/policy/marketing-third';
              queryparam = parsedQueryparam;
            }
            break;
          default:
            break;
        }
      }
      break;
    case 'device_management':
      {
        path = '/device-management';
        queryparam = parsedQueryparam;
      }
      break;
    case 'user_auth':
      {
        if (!parsedQueryparam.process_id) {
          throw new MalformedPathError();
        }

        path = '/auth/id/verify/start';
        queryparam = parsedQueryparam;
      }
      break;
    case 'gacha':
      {
        if (!(parsedQueryparam?.gacha_uid || parsedQueryparam?.map_uid)) {
          throw new MalformedPathError();
        }

        path = '/gacha';
        queryparam = parsedQueryparam;
      }
      break;
    case 'autocharge':
      {
        path = '/relay/autocharge';
        queryparam = parsedQueryparam;
      }
      break;
    case 'tvpoint':
      {
        path = '/relay/tvpoint';
        queryparam = parsedQueryparam;
      }
      break;
    case 'event':
      {
        const {hash_uid, ...rest} = parsedQueryparam;
        if (!hash_uid) {
          throw new MalformedPathError();
        }

        path = `/event/${hash_uid}`;
        queryparam = rest;
      }
      break;
    case 'welcome':
      {
        path = '/welcome';
        queryparam = parsedQueryparam;
      }
      break;
    case 'viewer':
      {
        const {series_id, product_id, ...rest} = parsedQueryparam;
        if (!series_id || !product_id) {
          throw new MalformedPathError();
        }

        path = `/content/${series_id}/viewer/${product_id}`;
        queryparam = rest;
      }
      break;
    /** @deprecated */
    case 'special-gift':
      {
        const {gift_id, auid, ...rest} = parsedQueryparam;
        if (!gift_id || !auid) {
          throw new MalformedPathError();
        }

        path = `/special-gift/reward/${gift_id}/${auid}`;
        queryparam = rest;
      }
      break;
    case 'maintenance':
      {
        path = '/maintenance';
        queryparam = parsedQueryparam;
      }
      break;

    case 'credit_refund':
      {
        const refundType = splittedPathList[3] || '';
        if (refundType !== 'agreement') {
          throw new MalformedPathError();
        }
        path = '/credit-refund/agreement';
        queryparam = parsedQueryparam;
      }
      break;

    case 'helix': {
      {
        const subactionPath = splittedPathList[3];
        if (subactionPath !== 'landing') {
          throw new MalformedPathError();
        }
        path = '/helix/landing';
        queryparam = parsedQueryparam;
      }
      break;
    }
    case 'ask': {
      {
        path = '/ask';
        queryparam = parsedQueryparam;
      }
      break;
    }
    case 'global': {
      if (splittedPathList.length < 4) {
        throw new MalformedPathError();
      }
      const subactionPath = splittedPathList[3];
      switch (subactionPath) {
        case 'age_auth': {
          path = '/auth/age/verify/start';
          queryparam = parsedQueryparam;
          break;
        }
        case 'login': {
          path = '/login';
          queryparam = parsedQueryparam;
          break;
        }
        case 'signup': {
          const {provider} = parsedQueryparam;
          if (!provider) {
            throw new MalformedPathError();
          }
          path = '/signup';
          queryparam = parsedQueryparam;
          break;
        }
        case 'user_agreement': {
          path = '/user/agreement';
          queryparam = parsedQueryparam;
          queryparam.is_global = 'true';
          break;
        }
        case 'policy': {
          if (splittedPathList.length < 5) {
            throw new MalformedPathError();
          }
          const globalPolicyType = splittedPathList[4];
          if (globalPolicyType === 'terms') {
            path = '/global-policy/terms';
            queryparam = parsedQueryparam;
          } else if (globalPolicyType === 'privacy') {
            path = '/global-policy/privacy';
            queryparam = parsedQueryparam;
          }
          break;
        }
      }
      break;
    }
    case 'error':
      {
        const errorType = splittedPathList[3] || '';

        switch (errorType) {
          case 'session_expired':
            {
              path = '/error/session-expired';
              queryparam = parsedQueryparam;
            }
            break;
          default:
            {
              path = '/error';
              queryparam = parsedQueryparam;
            }
            break;
        }
      }
      break;
    default: {
      throw new InvalidPathError();
    }
  }

  return new PagewebUrl({origin, path, queryparam});
}
